import { useEffect, useRef } from 'react';
import { DotLottie } from '@lottiefiles/dotlottie-web';

const reducedMotion = () =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export type PlayMode = 'loop' | 'once' | 'periodic' | 'static';

/** Режим по редкости (HANDOFF косметики п.1.2): 0 статика, 1 один проход,
 *  2 раз в ~10 с, 3 бесконечный цикл. Рамки — всегда цикл (исключение). */
export function playModeOf(rarity: number, slot: string): PlayMode {
  if (slot === 'frame') return 'loop';
  if (rarity >= 3) return 'loop';
  if (rarity === 2) return 'periodic';
  if (rarity === 1) return 'once';
  return 'static';
}

/**
 * Canvas-слой с .lottie: играет только на экране (IntersectionObserver),
 * reduced motion — статичный кадр (п.1.4: once — последний, прочие — 0).
 * layout 'fill' растягивает на весь холст; 'right' прижимает к правому краю
 * по высоте (эффекты у баллов).
 */
export function LottieLayer({
  src,
  mode,
  className,
  layout = 'fill',
  width,
  height,
  reverse = false,
}: {
  src: string;
  mode: PlayMode;
  className?: string;
  layout?: 'fill' | 'right';
  width: number;
  height: number;
  /** Проигрывать задом наперёд (ассеты, собранные «наоборот», напр. row_slot). */
  reverse?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let timer: number | null = null;
    const dl = new DotLottie({
      canvas,
      src,
      autoplay: false,
      loop: mode === 'loop',
      mode: reverse ? 'reverse' : 'forward',
      layout:
        layout === 'right'
          ? { fit: 'fit-height', align: [1, 0.5] }
          : { fit: 'fill', align: [0.5, 0.5] },
    });
    let visible = false;
    let played = false;

    const still = () => {
      // Статичный кадр: у «одного прохода» остаётся последний, у прочих — 0;
      // у реверса всё зеркально.
      const last = Math.max(0, dl.totalFrames - 1);
      const endFrame = mode === 'once' ? last : 0;
      dl.setFrame(reverse ? last - endFrame : endFrame);
      dl.pause();
    };
    const start = () => {
      if (!dl.isLoaded) return;
      if (mode === 'static' || reducedMotion()) return still();
      if (mode === 'once') {
        if (played) return still();
        played = true;
        dl.setLoop(false);
        dl.play();
        return;
      }
      if (mode === 'periodic') {
        dl.setLoop(false);
        // Случайная стартовая задержка 0–1,8 с (Handoff 3 п.5): соседние
        // элементы не мигают синхронно.
        window.setTimeout(() => {
          if (visible) dl.play();
        }, Math.random() * 1800);
        // Один интервал на жизнь слоя: повторный вход во вьюпорт не должен
        // плодить таймеры (иначе эпики играют чаще, чем раз в 10 с).
        if (timer === null) {
          timer = window.setInterval(() => {
            if (visible) {
              dl.setFrame(0);
              dl.play();
            }
          }, 10_000);
        }
        return;
      }
      dl.play();
    };

    dl.addEventListener('load', () => {
      if (visible) start();
      else still();
    });

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (!dl.isLoaded) return;
        if (visible) start();
        else dl.pause();
      },
      { rootMargin: '60px' },
    );
    io.observe(canvas);

    return () => {
      io.disconnect();
      if (timer !== null) window.clearInterval(timer);
      dl.destroy();
    };
    // src/mode фиксированы на жизнь слоя
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, mode, layout, reverse]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      width={width * 2}
      height={height * 2}
      aria-hidden
    />
  );
}
